import type { MouseEvent } from 'react';

import { Accordion, Flex } from '@grupoboticario/flora-react';

import { Card, ResponsiveFloraTypography } from 'components';
import { getFormattedPrice, isMobile as isMobileHelper, pluralize } from 'helpers';
import { useTracking } from 'hooks';

import Styles from './ItemsAccordion.styles';

import type { AccordionProps, KindProps } from './ItemsAccordion.d';
import type { Item } from 'domains/order.d';

export const AccordionCard = ({ type = 'standard', items = [] }: AccordionProps) => {
  const isMobile = isMobileHelper();
  const openOnDesk = items.length < 7;
  const { sendDataToGTM } = useTracking();
  const kind: KindProps = {
    standard: {
      title: pluralize(items.length, 'item comprado', 'itens comprados'),
      subtitle: 'Produtos que você comprou',
      analytics: 'itens-comprados',
    },
    gift: {
      title: pluralize(items.length, 'brinde', 'brindes'),
      subtitle: 'Produtos que você recebeu como brinde',
      analytics: 'brindes',
    },
    cut: {
      title: '',
      subtitle: '',
      analytics: 'itens-indisponiveis',
    },
  };
  const { analytics, title, subtitle } = kind[type];

  const handleTrackClick = (e: MouseEvent<HTMLButtonElement>) => {
    const typeStates = e.currentTarget.dataset.state as keyof typeof states;
    const states = {
      closed: 'abrir',
      open: 'fechar',
    };

    sendDataToGTM({
      event: 'analytics-event',
      event_parameters: {
        event_name: 'interaction_detalhes_pedido',
        cd_interaction_detail: `click:accordion:${states[typeStates]}`,
        cd_section: `${analytics}`,
      },
    });
  };

  return (
    <Card>
      <Accordion type='multiple' startOpen={openOnDesk && !isMobile}>
        <Styles.AccordionItem onClick={handleTrackClick} value='start'>
          <Styles.AccordionButton>
            <Flex direction='column'>
              <ResponsiveFloraTypography fontSize='subtitle' fontWeight='bold'>
                {items.length} {title}
              </ResponsiveFloraTypography>
              <ResponsiveFloraTypography>{subtitle}</ResponsiveFloraTypography>
            </Flex>
          </Styles.AccordionButton>
          <Styles.AccordionPanel>
            <Styles.Grid role='list'>
              {items?.map((item: Item) => (
                <Styles.itemCard key={item.sku} role='listitem'>
                  <Flex gap='$2' align='flex-start'>
                    <Styles.ImageWrapper src={item.imageUrl} alt={item.description} aria-hidden />
                    <Styles.ItemInfo direction='column' gap='$1'>
                      <ResponsiveFloraTypography
                        fontWeight='bold'
                        fontSize='exceptionsAuxiliar'
                        aria-label={`Código do produto: ${item.sku} `}
                      >
                        {item.sku}
                      </ResponsiveFloraTypography>
                      <Styles.ItemName fontSize='bodySmallShort'>
                        {item.description.toLowerCase()}
                      </Styles.ItemName>
                      <Flex gap='10%' wrap='wrap'>
                        <Flex
                          direction='column'
                          align='flex-start'
                          aria-label={`Quantidade: ${item.quantity}`}
                        >
                          <ResponsiveFloraTypography
                            fontSize='exceptionsAuxiliar'
                            fontWeight='bold'
                          >
                            Quantidade
                          </ResponsiveFloraTypography>
                          <ResponsiveFloraTypography fontSize='bodyLargeShort'>
                            {item.quantity}
                          </ResponsiveFloraTypography>
                        </Flex>
                        {Boolean(item.unitPrice) && (
                          <Flex
                            direction='column'
                            align='flex-start'
                            aria-label={`Valor unitário: ${getFormattedPrice(item.unitPrice)}`}
                          >
                            <ResponsiveFloraTypography
                              fontSize='exceptionsAuxiliar'
                              fontWeight='bold'
                            >
                              Valor unitário
                            </ResponsiveFloraTypography>
                            <ResponsiveFloraTypography fontSize='bodyLargeShort'>
                              {getFormattedPrice(item.unitPrice)}
                            </ResponsiveFloraTypography>
                          </Flex>
                        )}

                        <Flex
                          direction='column'
                          align='flex-start'
                          aria-label={`Subtotal: ${getFormattedPrice(item.totalPrice)}`}
                        >
                          <ResponsiveFloraTypography
                            fontSize='exceptionsAuxiliar'
                            fontWeight='bold'
                          >
                            Subtotal
                          </ResponsiveFloraTypography>
                          <ResponsiveFloraTypography fontSize='bodyLargeShort'>
                            {Boolean(item.totalPrice)
                              ? getFormattedPrice(item.totalPrice)
                              : 'Grátis'}
                          </ResponsiveFloraTypography>
                        </Flex>
                      </Flex>
                    </Styles.ItemInfo>
                  </Flex>
                </Styles.itemCard>
              ))}
            </Styles.Grid>
          </Styles.AccordionPanel>
        </Styles.AccordionItem>
      </Accordion>
    </Card>
  );
};
